// Traffic render pass: the line a player drew, and the aircraft flying it.
// Ported 1:1 out of `canvas.ts`'s `draw()`; only `this.ctx` → an explicit
// `frame.ctx`, and the Renderer's per-plane scratch state (`trails`,
// `routeHead`, `routeScratch` — reused every frame so drawing a route costs
// no per-frame allocation) is threaded in as parameters instead of `this.*`.
import type { Aircraft, Vec } from '../sim/index.ts';
import { hexA, reduceMotion } from './theme.ts';
import { roundRect } from './decor.ts';
import type { FrameCtx } from './canvas.ts';

export interface TrailDot { x: number; y: number; a: number }

export function drawPathLine(frame: FrameCtx, pts: Vec[], color: string, alpha: number): void {
  if (pts.length < 2) return;
  const { ctx } = frame;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = 4;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  // smooth quadratic curve through midpoints
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = (pts[i].x + pts[i + 1].x) / 2, my = (pts[i].y + pts[i + 1].y) / 2;
    ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
  }
  const last = pts[pts.length - 1];
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
  ctx.restore();
}

export function drawPlane(
  frame: FrameCtx,
  p: Aircraft,
  dragging: boolean,
  trails: Map<number, TrailDot[]>,
  routeHead: Vec,
  routeScratch: Vec[],
): void {
  const { ctx } = frame;
  const fade = p.fade;
  // Only the line the player drew is ever shown: `p.path` from the
  // aircraft's live position onward, minus its last `pathSnapped`
  // waypoints — the roll-in down the strip that `assignPath` appends when a
  // stroke stops at the bars. Those are flown but not drawn: they leave the
  // stroke at a fixed angle nobody drew, and the kink read as the game
  // breaking the player's own line.
  //
  // The renderer also deliberately does *not* draw the route the aircraft
  // will really fly (the turn-clipped projection `flownRoute`,
  // src/sim/preview.ts, still computes): finding out that a corner was too
  // sharp by watching the plane swing wide is the game, and a second guide
  // line drawn beside the player's own gave that away before the aircraft
  // had even started the turn.
  const drawn = p.path.length - p.pathSnapped;
  if (drawn > 0 && p.rollout === 0) {
    const a = dragging ? 0.45 : (p.landing ? 0.35 : 0.22);
    // Reused scratch: `routeHead` is mutated in place and `routeScratch`
    // truncated and refilled by reference, so drawing a route costs no
    // per-frame allocation (see the field comments above).
    routeHead.x = p.x; routeHead.y = p.y;
    routeScratch.length = 0;
    routeScratch.push(routeHead);
    for (let i = 0; i < drawn; i++) routeScratch.push(p.path[i]);
    drawPathLine(frame, routeScratch, p.type.color, a);
  }
  const dots = trails.get(p.id);
  if (dots) {
    for (const d of dots) {
      if (d.a <= 0) continue;
      ctx.fillStyle = hexA(p.type.color, Math.min(0.8, d.a) * fade);
      ctx.beginPath(); ctx.arc(d.x, d.y, 2.3, 0, Math.PI * 2); ctx.fill();
    }
  }
  if (p.holding && p.rollout === 0) {
    ctx.strokeStyle = hexA(p.type.color, 0.45);
    ctx.lineWidth = 1.6;
    ctx.setLineDash([5, 6]);
    ctx.beginPath(); ctx.arc(p.x, p.y, 24 * p.type.scale, 0, Math.PI * 2); ctx.stroke();
    ctx.setLineDash([]);
  }
  if (p.warn > 0) {
    const pu = reduceMotion ? 0.7 : (0.7 + 0.3 * Math.sin(performance.now() / 180));
    ctx.strokeStyle = `rgba(59,59,59,${0.6 * p.warn * pu})`;
    ctx.lineWidth = 2.2;
    ctx.beginPath(); ctx.arc(p.x, p.y, 30 * p.type.scale, 0, Math.PI * 2); ctx.stroke();
  }
  ctx.save();
  ctx.translate(p.x, p.y); ctx.rotate(p.ang);
  ctx.globalAlpha = fade;
  const s = (p.rollout > 0 ? (0.6 + 0.4 * fade) : 1) * p.type.scale;
  ctx.scale(s, s * (1 - 0.28 * Math.abs(p.bank)));
  // no `.bind()` here — that would allocate a fresh bound function every
  // frame per plane; call the two icon shapes directly instead.
  if (p.type.kind === 'heli') {
    ctx.save(); ctx.translate(2, 3); heliIcon(frame, 'rgba(61,74,82,0.13)', false); ctx.restore();
    heliIcon(frame, p.type.color, true);
  } else {
    ctx.save(); ctx.translate(2, 3); planeIcon(frame, 'rgba(61,74,82,0.13)', false); ctx.restore();
    planeIcon(frame, p.type.color, true);
  }
  ctx.restore();
  ctx.globalAlpha = 1;
}

function heliIcon(frame: FrameCtx, color: string, detail: boolean): void {
  const { ctx } = frame;
  ctx.fillStyle = color;
  // tail boom + rotor nub
  roundRect(ctx, -11.5, -1.2, 9, 2.4, 1.2); ctx.fill();
  ctx.beginPath(); ctx.arc(-11, 0, 2.1, 0, Math.PI * 2); ctx.fill();
  // body
  ctx.beginPath(); ctx.ellipse(1.5, 0, 6.2, 4.6, 0, 0, Math.PI * 2); ctx.fill();
  if (detail) {
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.beginPath(); ctx.ellipse(4, 0, 2.4, 2, 0, 0, Math.PI * 2); ctx.fill();
    const a = reduceMotion ? 0.6 : performance.now() / 90;
    ctx.strokeStyle = 'rgba(59,59,59,0.5)';
    ctx.lineWidth = 1.3; ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(1.5 - Math.cos(a) * 9.5, -Math.sin(a) * 9.5);
    ctx.lineTo(1.5 + Math.cos(a) * 9.5, Math.sin(a) * 9.5);
    ctx.moveTo(1.5 + Math.sin(a) * 9.5, -Math.cos(a) * 9.5);
    ctx.lineTo(1.5 - Math.sin(a) * 9.5, Math.cos(a) * 9.5);
    ctx.stroke();
    ctx.fillStyle = 'rgba(59,59,59,0.65)';
    ctx.beginPath(); ctx.arc(1.5, 0, 1.3, 0, Math.PI * 2); ctx.fill();
  }
}

function planeIcon(frame: FrameCtx, color: string, detail: boolean): void {
  const { ctx } = frame;
  ctx.fillStyle = color;
  // main wings — swept back with rounded tips
  ctx.beginPath();
  ctx.moveTo(4, -1.5);
  ctx.quadraticCurveTo(-0.5, -5.5, -3.2, -10.6);
  ctx.quadraticCurveTo(-4.4, -12.2, -5.2, -10.6);
  ctx.quadraticCurveTo(-5.6, -6, -5.6, -1.5);
  ctx.lineTo(-5.6, 1.5);
  ctx.quadraticCurveTo(-5.6, 6, -5.2, 10.6);
  ctx.quadraticCurveTo(-4.4, 12.2, -3.2, 10.6);
  ctx.quadraticCurveTo(-0.5, 5.5, 4, 1.5);
  ctx.closePath(); ctx.fill();
  // tailplane
  ctx.beginPath();
  ctx.moveTo(-7.6, -0.9);
  ctx.quadraticCurveTo(-9.6, -3.2, -11.4, -5.2);
  ctx.quadraticCurveTo(-12.4, -5.9, -12.2, -4.4);
  ctx.quadraticCurveTo(-11.7, -2.2, -11.6, -0.6);
  ctx.lineTo(-11.6, 0.6);
  ctx.quadraticCurveTo(-11.7, 2.2, -12.2, 4.4);
  ctx.quadraticCurveTo(-12.4, 5.9, -11.4, 5.2);
  ctx.quadraticCurveTo(-9.6, 3.2, -7.6, 0.9);
  ctx.closePath(); ctx.fill();
  // fuselage — rounded capsule with a soft nose
  ctx.beginPath(); ctx.ellipse(0.5, 0, 11.5, 3.1, 0, 0, Math.PI * 2); ctx.fill();
  if (detail) {
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.beginPath(); ctx.ellipse(7.6, 0, 2.2, 1.6, 0, 0, Math.PI * 2); ctx.fill();
  }
}
